import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';

const PURPLE = '#200854';
const ORANGE = '#FF6600';

interface OnboardingForm {
  name: string;
  surname: string;
  phone: string;
  riderLevel: string;
  averagePace: string;
  groupRidingExperience: string;
  bikeType: string;
  bike: string;
  dressSize: string;
}

const NajSportOnboarding: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('najsportUser');
    return stored ? JSON.parse(stored) : null;
  });

  const [form, setForm] = useState<OnboardingForm>(() => ({
    name: user?.name || '',
    surname: user?.surname || '',
    phone: '',
    riderLevel: '',
    averagePace: '',
    groupRidingExperience: '',
    bikeType: '',
    bike: '',
    dressSize: '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!user) {
    return null;
  }

  const isValid = form.name.trim() && form.surname.trim() && form.riderLevel && form.averagePace && form.groupRidingExperience;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSaving(true);
    setError('');
    try {
      const updated = await authAPI.updateMe({
        name: form.name.trim(),
        surname: form.surname.trim(),
        phone: form.phone.trim() || undefined,
        riderLevel: form.riderLevel,
        averagePace: form.averagePace,
        groupRidingExperience: form.groupRidingExperience === 'yes',
        bikeType: form.bikeType || undefined,
        bike: form.bike.trim() || undefined,
        dressSize: form.dressSize || undefined,
      });
      // Update localStorage with fresh read
      const currentStored = localStorage.getItem('najsportUser');
      const currentUser = currentStored ? JSON.parse(currentStored) : user;
      const u = { ...currentUser, name: updated.name, surname: updated.surname, onboardingCompleted: (updated as any).onboardingCompleted ?? true };
      localStorage.setItem('najsportUser', JSON.stringify(u));
      navigate('/events', { replace: true });
    } catch {
      setError(t('najsport.errorOnboarding'));
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500 text-sm";
  const labelClass = "block text-sm font-medium mb-1";

  return (
    <div className="min-h-screen flex flex-col" style={{ background: `linear-gradient(135deg, ${PURPLE} 0%, #1a0a3e 50%, #0f0625 100%)` }}>
      <header className="px-4 py-4 flex items-center justify-center">
        <img src="/najsport-logo.png" alt="NajSport" className="h-10 w-auto" />
      </header>

      <main className="flex-1 flex items-start justify-center px-4 pb-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg space-y-5 mt-4">
          <div className="text-center">
            <h2 className="text-xl font-bold" style={{ color: PURPLE }}>{t('najsport.welcomeTitle')}</h2>
            <p className="text-sm text-gray-500 mt-1">{t('najsport.welcomeSubtitle')}</p>
            <p className="text-xs text-gray-400 mt-1">{user.email}</p>
          </div>

          {/* Meno */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>
              {t('najsport.firstName')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputClass}
              placeholder={t('najsport.firstNamePlaceholder')}
            />
          </div>

          {/* Priezvisko */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>
              {t('najsport.lastName')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.surname}
              onChange={(e) => setForm({ ...form, surname: e.target.value })}
              className={inputClass}
              placeholder={t('najsport.lastNamePlaceholder')}
            />
          </div>

          {/* Telefón */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.phone')}</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className={inputClass}
              placeholder="+421 9XX XXX XXX"
            />
          </div>

          {/* Úroveň jazdca */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>
              {t('najsport.riderLevel')} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'beginner', label: t('najsport.beginner') },
                { value: 'intermediate', label: t('najsport.intermediate') },
                { value: 'advanced', label: t('najsport.advanced') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm({ ...form, riderLevel: opt.value })}
                  className={`py-2.5 px-2 rounded-lg text-xs font-medium border-2 transition-all ${
                    form.riderLevel === opt.value
                      ? 'text-white border-transparent'
                      : 'text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  style={form.riderLevel === opt.value ? { backgroundColor: PURPLE } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Priemerné tempo */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>
              {t('najsport.avgPace')} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'chill', label: t('najsport.chill') },
                { value: 'sport', label: t('najsport.sport') },
                { value: 'performance', label: t('najsport.performance') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm({ ...form, averagePace: opt.value })}
                  className={`py-2.5 px-2 rounded-lg text-xs font-medium border-2 transition-all ${
                    form.averagePace === opt.value
                      ? 'text-white border-transparent'
                      : 'text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  style={form.averagePace === opt.value ? { backgroundColor: PURPLE } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Skúsenosti s jazdou v skupine */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>
              {t('najsport.groupExperience')} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'yes', label: t('najsport.yes') },
                { value: 'no', label: t('najsport.no') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm({ ...form, groupRidingExperience: opt.value })}
                  className={`py-2.5 px-2 rounded-lg text-sm font-medium border-2 transition-all ${
                    form.groupRidingExperience === opt.value
                      ? 'text-white border-transparent'
                      : 'text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  style={form.groupRidingExperience === opt.value ? { backgroundColor: PURPLE } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Typ bicykla */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.bikeType')}</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'road', label: t('najsport.roadBike') },
                { value: 'gravel', label: t('najsport.gravel') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm({ ...form, bikeType: opt.value })}
                  className={`py-2.5 px-2 rounded-lg text-sm font-medium border-2 transition-all ${
                    form.bikeType === opt.value
                      ? 'text-white border-transparent'
                      : 'text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                  style={form.bikeType === opt.value ? { backgroundColor: PURPLE } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Bicykel - značka, model, rok */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.bikeDetails')}</label>
            <input
              type="text"
              value={form.bike}
              onChange={(e) => setForm({ ...form, bike: e.target.value })}
              className={inputClass}
              placeholder={t('najsport.bikeDetailsPlaceholder')}
            />
          </div>

          {/* Veľkosť dresu */}
          <div>
            <label className={labelClass} style={{ color: PURPLE }}>{t('najsport.jerseySize')}</label>
            <select
              value={form.dressSize}
              onChange={(e) => setForm({ ...form, dressSize: e.target.value })}
              className={inputClass}
            >
              <option value="">{t('najsport.selectSize')}</option>
              <option value="XS">XS</option>
              <option value="S">S</option>
              <option value="M">M</option>
              <option value="L">L</option>
              <option value="XL">XL</option>
              <option value="XXL">XXL</option>
            </select>
          </div>

          {error && (
            <div className="text-sm text-center py-2 rounded-lg bg-red-50 text-red-700">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !isValid}
            className="w-full py-3 rounded-lg text-white font-bold text-sm disabled:opacity-50 transition-all hover:shadow-lg"
            style={{ backgroundColor: ORANGE }}
          >
            {saving ? t('najsport.saving') : t('najsport.continue')}
          </button>

          <p className="text-xs text-gray-400 text-center">
            {t('najsport.requiredFields')}
          </p>

          <button
            type="button"
            onClick={() => {
              localStorage.removeItem('najsportUser');
              localStorage.removeItem('token');
              navigate('/', { replace: true });
            }}
            className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            {t('najsport.cancelAndLogout')}
          </button>
        </div>
      </main>
    </div>
  );
};

export default NajSportOnboarding;
